import { CheckIcon, PlaneIcon } from "lucide-react";
import { type ReactNode, useState } from "react";

import {
  Command,
  CommandCollection,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import { type AirlineHit, useAirlineSearch } from "../api/use-airline-search";

interface AirlineComboboxProps {
  label: ReactNode;
  value: string | null;
  onSelect: (airline: AirlineHit) => void;
}

/** Port of SearchAirline.vue — Algolia airline index combobox (scoped key client, active airlines only). */
export function AirlineCombobox({ label, value, onSelect }: AirlineComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<AirlineHit | null>(null);

  const { data: hits = [], isFetching } = useAirlineSearch(query);

  return (
    <div>
      <Label className="mb-1 block text-sm font-medium text-gray-900">{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          className={cn(
            "flex w-full items-center justify-between gap-x-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm",
            selected ? "text-gray-900" : "text-gray-400",
          )}
        >
          <span className="flex items-center gap-x-2 truncate">
            <PlaneIcon className="size-4 shrink-0 text-gray-500" aria-hidden="true" />
            {selected && value ? `${selected.name} (${selected.iata} / ${selected.icao})` : "Rechercher..."}
          </span>
          {selected && value ? (
            <CheckIcon className="size-4 shrink-0 text-green-600" aria-hidden="true" />
          ) : null}
        </PopoverTrigger>
        <PopoverContent align="start" className="w-(--anchor-width) min-w-64 p-0">
          {/* Hits are already filtered server-side by Algolia: filter={null}. */}
          <Command
            filter={null}
            items={hits}
            itemToStringValue={(airline) => (airline as AirlineHit).name}
            value={query}
            onValueChange={(next, details) => {
              // Selecting an item fills the input by default; keep the typed query instead.
              if (details.reason === "item-press") {
                return;
              }
              setQuery(next);
            }}
          >
            <CommandInput placeholder="Rechercher..." />
            {isFetching ? <div className="px-3 py-2 text-sm text-gray-500">Chargement...</div> : null}
            <CommandEmpty>Pas de résultat.</CommandEmpty>
            <CommandList>
              <CommandCollection>
                {(airline: AirlineHit) => (
                  <CommandItem
                    key={airline.objectID}
                    value={airline}
                    onClick={() => {
                      setSelected(airline);
                      onSelect(airline);
                      setOpen(false);
                    }}
                  >
                    <span
                      className={cn(
                        "inline-block size-2 shrink-0 rounded-full",
                        airline.isActive ? (airline.isEC261 ? "bg-blue-400" : "bg-green-400") : "bg-gray-200",
                      )}
                      aria-hidden="true"
                    />
                    <span className="mx-2 truncate">
                      {airline.name}
                      <span className="ml-2 font-semibold text-gray-500">{airline.iata}</span>
                    </span>
                  </CommandItem>
                )}
              </CommandCollection>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
